import { Component, DestroyRef, inject, OnInit, Renderer2 } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { JsonLdService } from './services/json-ld.service';
import { DOCUMENT, Location, ViewportScroller } from '@angular/common';
import { LanguageService } from './services/language/language.service';
import { MaintenanceService } from './services/maintenance.service';
import { PreventScrollService } from './services/prevent-scroll.service';
import { fromEvent } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { WindowResizeService } from './services/window-resize.service';
import { onWindowLoad } from './helpers/on-window-load.function';
import { PageRegistryService } from './services/page-registry.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { OopsPageComponent } from './pages/oops-page/oops-page.component';
import { AnnouncementComponent } from './components/announcement/announcement.component';
import { NavigationEnd, Router, RouterOutlet, Scroll } from '@angular/router';
import { CartModalComponent } from './components/cart-modal/cart-modal.component';
import { NotyComponent } from './components/noty/noty.component';
import { ButtonUpComponent } from './components/button-up/button-up.component';
import { DeviceService } from './services/device-detector/device.service';
import { AuthService } from './services/auth.service';
import { MANAGER_PHONES } from './constants';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [OopsPageComponent, AnnouncementComponent, RouterOutlet, CartModalComponent, NotyComponent, ButtonUpComponent]
})
export class AppComponent implements OnInit {
  private document = inject<Document>(DOCUMENT);
  private location = inject(Location);
  private jsonLdService = inject(JsonLdService);
  private languageService = inject(LanguageService);
  private maintenanceService = inject(MaintenanceService);
  private renderer = inject(Renderer2);
  private preventScrollService = inject(PreventScrollService);
  private windowResizeService = inject(WindowResizeService);
  private pageRegistryService = inject(PageRegistryService);
  private router = inject(Router);
  private viewportScroller = inject(ViewportScroller);
  private deviceService = inject(DeviceService);
  private authService = inject(AuthService);

  localBusinessJsonLd: SafeHtml;
  webSiteJsonLd: SafeHtml;

  get isMaintenanceInProgress(): boolean { return this.maintenanceService.isMaintenanceInProgress; }

  private readonly destroyRef = inject(DestroyRef);private lastNavigationUrl: string = '';

  constructor() {
    if (this.deviceService.isPlatformServer()) { return; }

    if (!this.isMaintenanceInProgress) {
      this.authService.fetchAndSetCustomer().subscribe();
    }

    /**
     * Custom implementation of "scrollPositionRestoration" to not execute scroll on anchor change
     */
    this.router.events
      .pipe(
        filter(e => e instanceof Scroll)
      )
      .subscribe((e) => {
        const scrollEvt = e as unknown as Scroll;
        const newNavigationUrl: string = scrollEvt.routerEvent instanceof NavigationEnd
          ? scrollEvt.routerEvent.urlAfterRedirects
          : scrollEvt.routerEvent.url;

        if (scrollEvt.position) {
          // backward navigation
          this.viewportScroller.scrollToPosition(scrollEvt.position);
        } else if (!scrollEvt.anchor) {
          const [lastUrl, _lastNavigationFragment] = this.lastNavigationUrl.split('#');
          if (lastUrl !== newNavigationUrl) {
            this.viewportScroller.scrollToPosition([0, 0]);
          }
        }

        this.lastNavigationUrl = newNavigationUrl;
      });
  }

  ngOnInit() {
    this.setLang();
    this.setJsonLd();

    onWindowLoad(() => {
      this.handlePreventScroll();
      this.handleWindowResize();
      this.handleInlinedCriticalCss();
      this.pageRegistryService.fetchPagesAndUpdateRouterConfig();
    });
  }

  private setLang() {
    const routeLang = this.location.path().slice(1, 3);
    this.languageService.setCurrentLangByRouteLang(routeLang);
  }

  private handlePreventScroll() {
    this.preventScrollService.isEnabled$.subscribe(isEnabled => {
      if (isEnabled) {
        this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
      } else {
        this.renderer.removeStyle(this.document.body, 'overflow');
      }
    });
  }

  private handleWindowResize() {
    fromEvent(window, 'resize')
      .pipe( debounceTime(500), takeUntilDestroyed(this.destroyRef) )
      .subscribe(() => this.windowResizeService.resized$.next());
  }

  private handleInlinedCriticalCss() {
    [].forEach.call(document.querySelectorAll('link[rel="stylesheet"][media="print"]'), link => {
      link.media = '';
      link.href += ''; // onload if not already loading
    });
  }

  private setJsonLd() {
    const localJsonLd = {
      '@context': "http://schema.org",
      '@type': "LocalBusiness",
      'address': {
        '@type': "PostalAddress",
        'addressLocality': "Киев",
        'addressRegion': "",
        'postalCode': "3600",
        'streetAddress': "",
      },
      'description': `В интернет-магазин "Клондайк" Вы найдёте широкий ассортимент товаров для позолотчиков, художников, мастериц, иконописцев и просто любителей хенд-мейда.
  ↵У нас можно купить такие товары как:
        ↵- товары для золочения (поталь, зеркальная поталь, мордан, шеллак, подушка для золочения, лампензель)
    ↵- художественные краски (акриловые краски, масляные краски, акварельные краски, гуашь, темпера, пастель)
    ↵- патины, воски и пигменты (битум, жидкая поталь, акриловые патины, восковые патины, металлические и художественные пигменты)
    ↵- синтетические и натуральные кисти (беличьи кисти, колонок, лиса, щетина)
    ↵- художественные аксессуары и инструменты (футляры для кистей, мастихины) и многое другое
  ↵Мы рады подобрать нужный Вам материал и доставить его в срок!`,
      'email': "info@klondike.com.ua",
      'faxNumber': MANAGER_PHONES[0],
      'image': "https://klondike.com.ua/media/seller_image/default/logo.png",
      'name': "Klondike",
      'priceRange': "$$$",
      'telephone': MANAGER_PHONES[0],
      'url': "https://klondike.com.ua/"
    };

    const websiteJsonLd = {
      '@context': "http://schema.org",
      '@type': "WebSite",
      'about': 'Интернет-магазин "Клондайк" Вы найдёте широкий ассортимент товаров для позолотчиков, художников, мастериц, иконописцев и просто любителей хенд-мейда.',
      'name': "Klondike",
      'url': "https://klondike.com.ua/"
    };

    this.localBusinessJsonLd = this.jsonLdService.getSafeJsonLd(localJsonLd);
    this.webSiteJsonLd = this.jsonLdService.getSafeJsonLd(websiteJsonLd);
  }

  // protected readonly AllMbiIcons = AllMbiIcons;
}
